import React, { useState } from 'react';
import { Palette, Plus, Trash2, Check, Copy, Sparkles, RefreshCw, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { BrandPalette, BrandColorSwatch, VdpTemplate, VdpElement } from '../../types';
import { 
  DEFAULT_BRAND_PALETTE, 
  BRAND_PALETTE_PRESETS, 
  isValidHex, 
  normalizeHex, 
  applyBrandPaletteToTemplate, 
  exportPaletteAsCss 
} from '../../utils/brandPaletteUtils';
import { useToast } from '../../store/ToastContext';

import { motion, AnimatePresence } from 'motion/react';
import { macSpring } from '../../utils/macAnimations';

interface BrandPaletteSectionProps {
  template: VdpTemplate;
  onUpdateTemplate: (updated: VdpTemplate) => void;
  selectedElements: VdpElement[];
  onUpdateElements: (updates: Partial<VdpElement>) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const BrandPaletteSection: React.FC<BrandPaletteSectionProps> = ({
  template,
  onUpdateTemplate,
  selectedElements,
  onUpdateElements,
  isExpanded,
  onToggleExpand,
}) => {
  const { toast } = useToast();
  const palette: BrandPalette = template.brandPalette || DEFAULT_BRAND_PALETTE;

  const [newSwatchName, setNewSwatchName] = useState('');
  const [newSwatchHex, setNewSwatchHex] = useState('#6366f1');
  const [showAddSwatch, setShowAddSwatch] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const updatePalette = (updates: Partial<BrandPalette>) => {
    const updatedPalette: BrandPalette = {
      ...palette,
      ...updates,
    };
    onUpdateTemplate({
      ...template,
      brandPalette: updatedPalette,
    });
  };

  const handleApplyColorToSelected = (color: string, targetProp: 'fill' | 'stroke' | 'both' = 'fill') => {
    if (selectedElements.length === 0) {
      toast.info('Brand Palette', 'Select an element on the canvas to apply this brand color.');
      return;
    }

    if (targetProp === 'fill') {
      onUpdateElements({ color, fillColor: color });
      toast.success('Brand Color Applied', `Applied ${color} as fill/text color`);
    } else if (targetProp === 'stroke') {
      onUpdateElements({ borderColor: color, strokeColor: color });
      toast.success('Brand Color Applied', `Applied ${color} as border/stroke color`);
    } else {
      onUpdateElements({ color, fillColor: color, borderColor: color, strokeColor: color });
      toast.success('Brand Color Applied', `Applied ${color} to selection`);
    }
  };

  const handleSelectPreset = (presetId: string) => {
    const preset = BRAND_PALETTE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;

    updatePalette(preset.palette);
    toast.success('Palette Selected', `Switched to "${preset.name}"`);
  };

  const handleApplyPaletteToAll = () => {
    const harmonized = applyBrandPaletteToTemplate(template, palette, {
      updateBackground: true,
      updateElements: true,
    });
    onUpdateTemplate(harmonized);
    toast.success('Brand Consistency Applied', 'Applied primary & secondary colors across all label elements');
  };

  const handleAddCustomSwatch = () => {
    if (!newSwatchName.trim()) {
      toast.error('Validation', 'Please provide a name for the custom swatch');
      return;
    }
    const cleanHex = normalizeHex(newSwatchHex);
    if (!isValidHex(cleanHex)) {
      toast.error('Validation', 'Please enter a valid hex color (e.g. #2563EB)');
      return;
    }

    const newSwatch: BrandColorSwatch = {
      id: `sw-${Date.now()}`,
      name: newSwatchName.trim(),
      hex: cleanHex,
      type: 'custom',
    };

    const swatches = [...(palette.swatches || []), newSwatch];
    updatePalette({ swatches });
    setNewSwatchName('');
    setShowAddSwatch(false);
    toast.success('Swatch Saved', `Added "${newSwatch.name}" (${newSwatch.hex}) to brand palette`);
  };

  const handleDeleteSwatch = (id: string) => {
    const swatches = (palette.swatches || []).filter((s) => s.id !== id);
    updatePalette({ swatches });
    toast.info('Swatch Removed', 'Brand color swatch deleted');
  };

  const handleCopyCssVariables = () => {
    const css = exportPaletteAsCss(palette);
    navigator.clipboard.writeText(css);
    setCopiedToken('css');
    setTimeout(() => setCopiedToken(null), 2000);
    toast.success('Copied Tokens', 'Brand CSS variables copied to clipboard');
  };

  return (
    <div className="border-b border-zinc-800 bg-zinc-950" id="tour-brand-palette">
      {/* Section Header Accordion */}
      <div
        onClick={onToggleExpand}
        className="p-3.5 flex items-center justify-between cursor-pointer select-none hover:bg-zinc-900/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="p-0.5 text-zinc-400 hover:text-white transition-colors">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 text-pink-400" />
            <h3 className="font-semibold text-zinc-200 text-sm">Brand Color Palette</h3>
          </div>
        </div>

        {/* Quick Color Indicators */}
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <div
            className="w-3.5 h-3.5 rounded-full border border-zinc-700 shadow-xs"
            style={{ backgroundColor: palette.primary }}
            title={`Primary: ${palette.primary}`}
          />
          <div
            className="w-3.5 h-3.5 rounded-full border border-zinc-700 shadow-xs"
            style={{ backgroundColor: palette.secondary }}
            title={`Secondary: ${palette.secondary}`}
          />
          {palette.accent && (
            <div
              className="w-3.5 h-3.5 rounded-full border border-zinc-700 shadow-xs"
              style={{ backgroundColor: palette.accent }}
              title={`Accent: ${palette.accent}`}
            />
          )}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={macSpring}
            className="px-4 pb-4 pt-1 border-t border-zinc-800/60 flex flex-col gap-3.5 overflow-hidden"
          >
          {/* Preset Theme Selector */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">
                Theme Presets
              </label>
              <span className="text-[10px] text-zinc-500 font-medium">1-Click Curated</span>
            </div>
            <select
              value={BRAND_PALETTE_PRESETS.find((p) => p.name === palette.name)?.id || ''}
              onChange={(e) => handleSelectPreset(e.target.value)}
              className="w-full px-2.5 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200 outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
            >
              <option value="" disabled>
                -- Choose Preset Palette --
              </option>
              {BRAND_PALETTE_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.name}
                </option>
              ))}
            </select>
          </div>

          {/* Primary & Secondary Core Brand Colors Grid */}
          <div className="grid grid-cols-2 gap-2.5">
            {/* Primary Color Card */}
            <div className="p-2.5 bg-zinc-900/90 rounded-lg border border-zinc-800 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-300 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                  Primary
                </span>
                {selectedElements.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleApplyColorToSelected(palette.primary, 'fill')}
                    className="text-[9px] px-1.5 py-0.5 bg-blue-950/80 hover:bg-blue-900 text-blue-300 rounded border border-blue-800/50 transition-colors"
                    title="Apply to selected elements"
                  >
                    Apply
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <div className="relative w-8 h-8 rounded-md border border-zinc-700 overflow-hidden shrink-0 shadow-inner">
                  <input
                    type="color"
                    value={palette.primary}
                    onChange={(e) => updatePalette({ primary: e.target.value })}
                    className="absolute -inset-2 w-12 h-12 cursor-pointer border-0 p-0"
                    title="Pick Primary Color"
                  />
                </div>
                <input
                  type="text"
                  value={palette.primary}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith('#') || val === '') {
                      updatePalette({ primary: val });
                    }
                  }}
                  className="w-full px-2 py-1 bg-zinc-950 border border-zinc-700/80 rounded text-[11px] font-mono text-zinc-200 uppercase outline-none focus:border-blue-500"
                  placeholder="#2563EB"
                  maxLength={7}
                />
              </div>
            </div>

            {/* Secondary Color Card */}
            <div className="p-2.5 bg-zinc-900/90 rounded-lg border border-zinc-800 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-zinc-300 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                  Secondary
                </span>
                {selectedElements.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleApplyColorToSelected(palette.secondary, 'fill')}
                    className="text-[9px] px-1.5 py-0.5 bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 rounded border border-emerald-800/50 transition-colors"
                    title="Apply to selected elements"
                  >
                    Apply
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <div className="relative w-8 h-8 rounded-md border border-zinc-700 overflow-hidden shrink-0 shadow-inner">
                  <input
                    type="color"
                    value={palette.secondary}
                    onChange={(e) => updatePalette({ secondary: e.target.value })}
                    className="absolute -inset-2 w-12 h-12 cursor-pointer border-0 p-0"
                    title="Pick Secondary Color"
                  />
                </div>
                <input
                  type="text"
                  value={palette.secondary}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.startsWith('#') || val === '') {
                      updatePalette({ secondary: val });
                    }
                  }}
                  className="w-full px-2 py-1 bg-zinc-950 border border-zinc-700/80 rounded text-[11px] font-mono text-zinc-200 uppercase outline-none focus:border-emerald-500"
                  placeholder="#10B981"
                  maxLength={7}
                />
              </div>
            </div>
          </div>

          {/* Accent & Neutral Minor Tones */}
          <div className="p-2.5 bg-zinc-900/50 rounded-lg border border-zinc-800/80 flex items-center justify-between gap-2">
            {/* Accent */}
            <div className="flex items-center gap-1.5">
              <div className="relative w-5 h-5 rounded border border-zinc-700 overflow-hidden shrink-0">
                <input
                  type="color"
                  value={palette.accent || '#f59e0b'}
                  onChange={(e) => updatePalette({ accent: e.target.value })}
                  className="absolute -inset-2 w-9 h-9 cursor-pointer border-0 p-0"
                  title="Accent Color"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-zinc-400 font-medium">Accent</span>
                <span className="text-[9px] font-mono text-zinc-500">{palette.accent || '#f59e0b'}</span>
              </div>
            </div>

            {/* Background */}
            <div className="flex items-center gap-1.5">
              <div className="relative w-5 h-5 rounded border border-zinc-700 overflow-hidden shrink-0">
                <input
                  type="color"
                  value={palette.background || '#ffffff'}
                  onChange={(e) => updatePalette({ background: e.target.value })}
                  className="absolute -inset-2 w-9 h-9 cursor-pointer border-0 p-0"
                  title="Canvas Background"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-zinc-400 font-medium">Card Bg</span>
                <span className="text-[9px] font-mono text-zinc-500">{palette.background || '#ffffff'}</span>
              </div>
            </div>

            {/* Neutral Text */}
            <div className="flex items-center gap-1.5">
              <div className="relative w-5 h-5 rounded border border-zinc-700 overflow-hidden shrink-0">
                <input
                  type="color"
                  value={palette.neutral || '#0f172a'}
                  onChange={(e) => updatePalette({ neutral: e.target.value })}
                  className="absolute -inset-2 w-9 h-9 cursor-pointer border-0 p-0"
                  title="Neutral / Text Color"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-zinc-400 font-medium">Neutral</span>
                <span className="text-[9px] font-mono text-zinc-500">{palette.neutral || '#0f172a'}</span>
              </div>
            </div>
          </div>

          {/* Custom Brand Swatches Collection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">
                Saved Brand Swatches
              </label>
              <button
                type="button"
                onClick={() => setShowAddSwatch(!showAddSwatch)}
                className="text-[10px] text-pink-400 hover:text-pink-300 font-medium flex items-center gap-1 transition-colors cursor-pointer"
              >
                <Plus className="w-3 h-3" /> Add Swatch
              </button>
            </div>

            {/* Add Swatch Inline Form */}
            {showAddSwatch && (
              <div className="p-2 bg-zinc-900 border border-zinc-700 rounded-lg mb-2 flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="relative w-7 h-7 rounded border border-zinc-600 overflow-hidden shrink-0">
                    <input
                      type="color"
                      value={newSwatchHex}
                      onChange={(e) => setNewSwatchHex(e.target.value)}
                      className="absolute -inset-2 w-11 h-11 cursor-pointer"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Swatch Name (e.g. Amber 500)"
                    value={newSwatchName}
                    onChange={(e) => setNewSwatchName(e.target.value)}
                    className="flex-1 px-2 py-1 bg-zinc-950 border border-zinc-700 rounded text-xs text-zinc-200 outline-none"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomSwatch}
                    className="px-2.5 py-1 bg-pink-600 hover:bg-pink-500 text-white rounded text-xs font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}

            {/* Swatches Grid */}
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto custom-scrollbar p-1 bg-zinc-950/60 rounded-md border border-zinc-800">
              {/* Primary & Secondary default pills */}
              <button
                type="button"
                onClick={() => handleApplyColorToSelected(palette.primary, 'fill')}
                className="group flex items-center gap-1.5 px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 rounded text-[11px] text-zinc-200 transition-all cursor-pointer shadow-xs"
                title={`Primary: ${palette.primary} (Click to apply)`}
              >
                <span className="w-3 h-3 rounded-full border border-zinc-700 shrink-0" style={{ backgroundColor: palette.primary }} />
                <span className="font-medium">Primary</span>
              </button>

              <button
                type="button"
                onClick={() => handleApplyColorToSelected(palette.secondary, 'fill')}
                className="group flex items-center gap-1.5 px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 rounded text-[11px] text-zinc-200 transition-all cursor-pointer shadow-xs"
                title={`Secondary: ${palette.secondary} (Click to apply)`}
              >
                <span className="w-3 h-3 rounded-full border border-zinc-700 shrink-0" style={{ backgroundColor: palette.secondary }} />
                <span className="font-medium">Secondary</span>
              </button>

              {palette.accent && (
                <button
                  type="button"
                  onClick={() => handleApplyColorToSelected(palette.accent!, 'fill')}
                  className="group flex items-center gap-1.5 px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 rounded text-[11px] text-zinc-200 transition-all cursor-pointer shadow-xs"
                  title={`Accent: ${palette.accent} (Click to apply)`}
                >
                  <span className="w-3 h-3 rounded-full border border-zinc-700 shrink-0" style={{ backgroundColor: palette.accent }} />
                  <span className="font-medium">Accent</span>
                </button>
              )}

              {/* Custom Swatches */}
              {(palette.swatches || []).map((swatch) => (
                <div
                  key={swatch.id}
                  className="group flex items-center gap-1.5 px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700/80 rounded text-[11px] text-zinc-200 transition-all shadow-xs"
                >
                  <button
                    type="button"
                    onClick={() => handleApplyColorToSelected(swatch.hex, 'fill')}
                    className="flex items-center gap-1.5 cursor-pointer"
                    title={`Apply ${swatch.name} (${swatch.hex})`}
                  >
                    <span className="w-3 h-3 rounded-full border border-zinc-700 shrink-0" style={{ backgroundColor: swatch.hex }} />
                    <span className="truncate max-w-[70px]">{swatch.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteSwatch(swatch.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-500 hover:text-red-400 transition-opacity"
                    title="Delete Swatch"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons: 1-Click Brand Consistency & CSS Export */}
          <div className="flex flex-col gap-2 pt-1 border-t border-zinc-800/80">
            <button
              type="button"
              onClick={handleApplyPaletteToAll}
              className="w-full py-1.5 px-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded text-xs font-semibold shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              title="Apply brand palette across all template headers, shapes, and borders"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-300" />
              Harmonize All Elements
            </button>

            <button
              type="button"
              onClick={handleCopyCssVariables}
              className="w-full py-1 px-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white rounded text-[11px] font-medium transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              {copiedToken === 'css' ? (
                <>
                  <Check className="w-3 h-3 text-emerald-400" />
                  <span>Tokens Copied to Clipboard</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 text-zinc-400" />
                  <span>Copy CSS Brand Tokens</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </div>
  );
};
